import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import AppConfigList from "@saleor/appConfiguration/components/AppConfigList";
import AppConfigUploadDialog from "@saleor/appConfiguration/components/AppConfigUploadDialog";
import { useAppConfigCreate } from "@saleor/appConfiguration/mutations";
import { useAppConfigurationsListQuery } from "@saleor/appConfiguration/queries";
import {
  appConfigurationListUrl,
  appConfigurationUrl,
  AppConfigurationUrlQueryParams
} from "@saleor/appConfiguration/url";
import AppHeader from "@saleor/components/AppHeader";
import Container from "@saleor/components/Container";
import PageHeader from "@saleor/components/PageHeader";
import { DEFAULT_INITIAL_PAGINATION_DATA } from "@saleor/config";
import { configurationMenuUrl } from "@saleor/configuration";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import { commonMessages, sectionNames } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import { ListViews } from "@saleor/types";
import { AppConfigOrderField, OrderDirection } from "@saleor/types/globalTypes";
import _ from "lodash";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

export interface AppConfigurationListPageProps {
  params: AppConfigurationUrlQueryParams;
}

export const AppConfigurationListPage: React.FC<AppConfigurationListPageProps> = ({
  params
}) => {
  const notify = useNotifier();
  const intl = useIntl();
  const paginate = usePaginator();
  const navigate = useNavigator();
  const [uploadLoading, setUploadLoading] = useState(false);

  const { settings, updateListSettings } = useListSettings(
    ListViews.APPCONFIG_LIST
  );

  const paginationState = createPaginationState(settings.rowNumber, params);

  const queryVariables = React.useMemo(
    () => ({
      ...paginationState
    }),
    [params]
  );

  React.useEffect(
    () =>
      navigate(
        appConfigurationListUrl({
          ...params,
          ...DEFAULT_INITIAL_PAGINATION_DATA
        }),
        true
      ),
    [settings.rowNumber]
  );

  const [appConfigCreate, appConfigCreateOpts] = useAppConfigCreate({
    onCompleted: data => {
      if (data.appConfigCreate.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "App successfully created",
            id: "appSuccessSnackBar"
          })
        });
      } else if (
        data.appConfigCreate?.errors[0].code === "INVALID" &&
        data.appConfigCreate?.errors[0].field === "version"
      ) {
        notify({
          status: "warning",
          text: data?.appConfigCreate?.errors[0].message
        });
      } else {
        notify({
          status: "error",
          text: intl.formatMessage(commonMessages.somethingWentWrong)
        });
      }
      refetch();
      setUploadLoading(false);
      closeModal();
    }
  });

  const closeModal = () => navigate(appConfigurationListUrl(), true);

  const sort = {
    sort: {
      direction: OrderDirection.DESC,
      field: AppConfigOrderField.VERSION
    }
  };

  const filter = {
    filter: {
      unique: true
    }
  };

  const { data, loading, refetch } = useAppConfigurationsListQuery({
    displayLoader: true,
    variables: {
      ...queryVariables,
      ...sort,
      ...filter
    }
  });

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    maybe(() => data.appConfigs.pageInfo),
    paginationState,
    params
  );

  return (
    <>
      <Container>
        <AppHeader onBack={() => navigate(configurationMenuUrl)}>
          {intl.formatMessage(sectionNames.configuration)}
        </AppHeader>
        <PageHeader title={intl.formatMessage(sectionNames.appConfiguration)}>
          <Button
            id="createAppBtn"
            color="primary"
            variant="contained"
            onClick={() =>
              navigate(
                appConfigurationListUrl({
                  action: "create"
                })
              )
            }
          >
            <FormattedMessage
              defaultMessage="Create App"
              description="button"
            />
          </Button>
        </PageHeader>
        <Card>
          <AppConfigList
            settings={settings}
            appConfigs={maybe(() =>
              data.appConfigs.edges.map(edge => edge.node)
            )}
            onNextPage={loadNextPage}
            onPreviousPage={loadPreviousPage}
            onUpdateListSettings={updateListSettings}
            pageInfo={pageInfo}
            toolbar={""}
            disabled={loading}
            toggle={function(id: string): void {
              throw new Error(id ? "Function not implemented." : "implemented");
            }}
            toggleAll={function(
              items: React.ReactNodeArray,
              selected: number
            ): void {
              throw new Error(
                items || selected ? "Function not implemented." : "implemented"
              );
            }}
            isChecked={function(id: string): boolean {
              throw new Error(id ? "Function not implemented." : "implemented");
            }}
            selected={0}
            onRowClick={id => () => navigate(appConfigurationUrl(id))}
          />
        </Card>
      </Container>

      <AppConfigUploadDialog
        open={params.action === "create"}
        params={params.action}
        loading={appConfigCreateOpts.loading || uploadLoading}
        onFileError={() => {
          notify({
            status: "error",
            text: intl.formatMessage({
              defaultMessage: "Please upload JSON files only."
            }),
            title: intl.formatMessage({
              defaultMessage: "Can't upload file!",
              description: "import, header"
            })
          });
        }}
        confirmButtonState={"loading"}
        disabled={loading}
        errors={[]}
        onClose={closeModal}
        onSave={formdata => {
          appConfigCreate({
            variables: { json: formdata.json, name: formdata.name }
          });
        }}
      />
    </>
  );
};

export default AppConfigurationListPage;
